import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Play } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { scrollToId } from "@/hooks/useReveal";

/**
 * Cinematic video hero.
 * - autoplay muted loop (clip is silent — no mute toggle shown)
 * - SHORT pinned section (110vh) so initial scrolling doesn't feel trapped
 * - scroll-tied subtle dim + copy translate (no scale to avoid jank)
 * - prefers-reduced-motion → static poster image
 * - graceful onError fallback to poster
 */
export function HeroVideo() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reducedMotion) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [reducedMotion]);

  // Scroll-tied parallax
  useEffect(() => {
    let raf;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const el = sectionRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const total = Math.max(el.offsetHeight - vh, 1);
        const p = Math.min(Math.max(-r.top / total, 0), 1);
        setScrollPct(p);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Visual transforms — only subtle ones (no scale to avoid jitter on macOS smooth-scroll)
  const dim = 0.35 + scrollPct * 0.4;
  const copyShift = scrollPct * -24;

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative w-full"
      style={{ height: "110vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* VIDEO LAYER */}
        {!reducedMotion && !videoFailed ? (
          <video
            ref={videoRef}
            data-testid={HERO.canvas}
            className="absolute inset-0 w-full h-full object-cover"
            src="/media/hero.mp4"
            poster="/assets/generated/hero_neural_background_text_safe.webp"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <picture>
            <source srcSet="/assets/generated/hero_neural_background_text_safe.avif" type="image/avif" />
            <source srcSet="/assets/generated/hero_neural_background_text_safe.webp" type="image/webp" />
            <img
              data-testid={HERO.fallback}
              src="/assets/generated/hero_neural_background_text_safe.png"
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
        )}

        {/* Ambient orbs */}
        <div
          className="pointer-events-none absolute -top-24 -left-20 h-[520px] w-[520px] rounded-full opacity-40 nb-drift"
          style={{
            background:
              "radial-gradient(circle, rgba(56,189,248,0.32) 0%, transparent 60%)",
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-24 -right-10 h-[560px] w-[560px] rounded-full opacity-45 nb-drift"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.32) 0%, transparent 60%)",
            animationDelay: "-6s",
          }}
        />

        {/* Stronger left scrim so headline (right side) stays crisp */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(2,6,23,0.94) 0%, rgba(2,6,23,0.82) 20%, rgba(2,6,23,0.45) 38%, rgba(2,6,23,0.05) 55%, rgba(2,6,23,0.1) 65%, rgba(2,6,23,0.6) 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `linear-gradient(180deg, rgba(2,6,23,0.6) 0%, rgba(2,6,23,${
              dim * 0.2
            }) 25%, rgba(2,6,23,${dim * 0.15}) 70%, rgba(2,6,23,0.95) 100%)`,
          }}
        />

        {/* Hero copy */}
        <div
          className="relative z-10 h-full max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12"
          style={{ transform: `translate3d(0, ${copyShift}px, 0)` }}
        >
          <div className="lg:col-span-7 lg:col-start-6 flex flex-col justify-center pt-24">
            <div className="reveal in flex flex-wrap items-center gap-3 mb-7">
              <span className="pill">
                <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8] nb-pulse" />
                Research-stage neurotechnology
              </span>
              <span className="hidden md:inline-flex pill !border-white/10">
                <span className="h-1.5 w-1.5 rounded-full bg-[#8B5CF6]" />
                Pre-clinical · Founding round
              </span>
            </div>

            <h1
              data-testid={HERO.headline}
              className="font-display text-white headline-glow text-[clamp(40px,7vw,84px)] leading-[1.02] font-medium tracking-tight"
            >
              Rewire, <span className="text-[#BFDBFE]">Refine,</span>{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg,#38BDF8 0%, #6366F1 55%, #8B5CF6 100%)",
                }}
              >
                Redefine
              </span>
              .
            </h1>

            <p className="mt-6 text-[#CBD5E1] text-[17px] lg:text-[19px] max-w-[640px] leading-relaxed">
              AI-powered neuroplasticity through biocompatible brain-interface
              technology.
            </p>

            <p className="mt-5 text-[#94A3B8] text-[14px] lg:text-[15px] max-w-[620px] leading-relaxed">
              Neurobolt.AI is building a research-stage platform to map neural
              activity, model circuit behavior, and guide adaptive biofeedback
              for the next generation of brain health, rehabilitation, and
              human performance.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <button
                data-testid={HERO.ctaPlatform}
                onClick={() => scrollToId("platform")}
                className="btn-glow group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full bg-white text-[#020617] font-semibold text-[14px] tracking-wide hover:bg-[#BFDBFE] transition-colors"
              >
                Explore the platform
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
              <Link
                data-testid={HERO.ctaScience}
                to="/research"
                className="group inline-flex items-center gap-2 pl-5 pr-4 py-3 rounded-full border border-white/15 text-white text-[14px] font-medium hover:border-[#38BDF8]/60 hover:bg-white/[0.04] transition-all"
              >
                View the science
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1 text-[#38BDF8]"
                />
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[12px] font-mono uppercase tracking-[0.2em] text-[#64748B]">
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#38BDF8]" />
                Closed-loop
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#6366F1]" />
                Biocompatible
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#8B5CF6]" />
                Adaptive AI
              </span>
            </div>
          </div>
        </div>

        {/* HUD: clip identifier */}
        <div className="absolute bottom-8 left-6 lg:left-12 z-10 flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
            Loop
          </span>
          <span className="inline-flex items-center gap-2 text-white/80">
            <Play size={12} className="text-[#38BDF8]" />
            <span className="font-mono text-[11px] tracking-wider">NB-001</span>
          </span>
        </div>

        <div className="hidden lg:flex absolute bottom-8 right-12 z-10 items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#64748B] animate-bounce">
          Scroll <ChevronDown size={14} />
        </div>
      </div>
    </section>
  );
}
